import { Typography } from '@euler/app/components/typography';
import { VerticalLine } from '@euler/app/tv/components/VerticalLine';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { MaybeText } from '@euler/components/MaybeText';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import { kFrontRecordsSiteCode, kRearRecordsSiteCode } from '@euler/model';
import {
  AbnormalLevel,
  CommonTaskStatus,
  SiteInspectionType,
} from '@euler/model/enum';
import { PreInspection } from '@euler/model/task-detail';
import { dateFromValue } from '@euler/utils/datetime';
import { timeUtilNow } from '@euler/utils/formatters';
import { ReactNode, memo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

export const PreInspectionSummaryView = memo(
  ({
    preInspection,
    inventoryQuery,
    style,
  }: {
    preInspection: PreInspection;
    inventoryQuery: InventoryQuery;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const { status, finishedAt } = preInspection;
    const statusText =
      status === CommonTaskStatus.Pending
        ? '未开始'
        : status === CommonTaskStatus.InProgress
        ? '检测中'
        : '已完成';
    const timestamp =
      finishedAt ??
      Math.max(
        ...preInspection.flows.map(x =>
          dateFromValue(x.finishedAt ?? x.updatedAt ?? x.createdAt).getTime(),
        ),
      );

    let totalCount = 0;
    let defectiveCount = 0;
    let frontRecordCount = 0;
    let rearRecordCount = 0;

    for (const inspectedSite of preInspection.sites) {
      if (inspectedSite.inspectionType !== SiteInspectionType.Facade) {
        continue;
      }
      totalCount++;
      if (inspectedSite.abnormalLevel !== AbnormalLevel.Fine) {
        defectiveCount++;
      }
      const site = inventoryQuery.getSiteById(inspectedSite.siteId);
      if (site != null) {
        if (site.code === kFrontRecordsSiteCode) {
          frontRecordCount++;
        } else if (site.code === kRearRecordsSiteCode) {
          rearRecordCount++;
        }
      }
    }

    return (
      <HStack
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: stretch;
        `}
        style={style}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          <InfoCell label="检测状态" text={statusText} />
          <InfoCell label="全部检测" text={`${totalCount}`} />
          <InfoCell
            label="前部记录"
            text={`${frontRecordCount}`}
            css={`
              margin-bottom: 0;
            `}
          />
        </View>
        <VerticalLine
          startColor={theme.panel.separatorStartColor}
          endColor={theme.panel.separatorEndColor}
          css={`
            margin: ${rpx(2)}px 0;
          `}
        />
        <View
          css={`
            flex: 1;
          `}
        >
          <InfoCell
            label="更新时间"
            text={
              timestamp != null ? timeUtilNow(timestamp, 'MM/dd HH:mm') : '-'
            }
          />
          <InfoCell label="异常结果" text={`${defectiveCount}`} />

          <InfoCell
            label="后部记录"
            text={`${rearRecordCount}`}
            css={`
              margin-bottom: 0;
            `}
          />
        </View>
      </HStack>
    );
  },
);

const InfoCell = memo(
  ({
    text,
    label,
    style,
  }: {
    text: ReactNode | string;
    label: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          align-items: center;
          margin-bottom: ${rpx(29)}px;
        `}
        style={[style]}
      >
        <View
          css={`
            margin-bottom: ${rpx(4)}px;
          `}
        >
          <MaybeText
            text={text}
            numberOfLines={1}
            css={`
              color: ${theme.panel.infoValueColor};
              font-size: ${rpx(28)}px;
            `}
          />
        </View>
        <Typography.Label color={theme.panel.infoLabelColor} size={rpx(18)}>
          {label}
        </Typography.Label>
      </View>
    );
  },
);
